import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Database, Plus, TrendingUp, BarChart3, Banknote, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = ['prospect', 'data_review', 'contract_sent', 'signed', 'live', 'churned'];
const STAGE_COLORS = {
  prospect:      'bg-slate-100 text-slate-700',
  data_review:   'bg-purple-100 text-purple-800',
  contract_sent: 'bg-amber-100 text-amber-800',
  signed:        'bg-blue-100 text-blue-800',
  live:          'bg-emerald-100 text-emerald-800',
  churned:       'bg-rose-100 text-rose-800',
};
const SKU_LABELS = {
  benchmark_subscription:      'Benchmark · $150K/yr',
  pharma_device_comarketing:   'Pharma / device · $1.5M/yr',
  rwe_actuarial:               'RWE / actuarial · $3M/yr',
  hospital_referral_dashboard: 'Hospital dashboards · $250K/yr',
};

const fmtUSD = (n) => {
  if (n == null) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

/** /admin/data-network — Layer-7 Data Network portfolio (Blueprint #43). */
export default function AdminDataNetwork() {
  const { getAuthHeader } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    counterparty_name: '', counterparty_type: 'pharma', sku: 'benchmark_subscription', quantity: 1, stage: 'prospect',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/admin/data-network/contracts`, getAuthHeader()),
        axios.get(`${API}/admin/data-network/portfolio`, getAuthHeader()),
      ]);
      setContracts(a.data.contracts || []);
      setPortfolio(b.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load data-network portfolio');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.counterparty_name) { toast.error('Counterparty required'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/data-network/contracts`, {
        ...form,
        quantity: Number(form.quantity),
      }, getAuthHeader());
      toast.success('Contract created');
      setShowCreate(false);
      setForm({ counterparty_name: '', counterparty_type: 'pharma', sku: 'benchmark_subscription', quantity: 1, stage: 'prospect' });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const advance = async (id, st) => {
    const next = STAGES[Math.min(STAGES.length - 2, STAGES.indexOf(st) + 1)];
    if (next === st) return;
    try {
      await axios.patch(`${API}/admin/data-network/contracts/${id}`, { stage: next }, getAuthHeader());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const archive = async (id) => {
    if (!window.confirm('Archive this contract?')) return;
    try {
      await axios.delete(`${API}/admin/data-network/contracts/${id}`, getAuthHeader());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Archive failed'); }
  };

  return (
    <DashboardShell data-testid="data-network-page">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="cn-eyebrow inline-flex items-center gap-1.5">
            <Database className="w-3 h-3" /> Layer 7 · Data Network
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Data Network portfolio</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Anonymized benchmark + RWE + pharma + hospital data products. Veeva-grade ~85 % gross margin.
          </p>
        </div>
        <Button onClick={() => setShowCreate((s) => !s)} className="bg-cn-primary text-white hover:bg-cn-primary-hover" data-testid="dn-create-toggle">
          <Plus className="w-4 h-4 mr-2" /> New contract
        </Button>
      </header>

      {portfolio && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="dn-tiles">
          <Tile label="Live ARR" value={fmtUSD(portfolio.live_annual_revenue)} icon={<TrendingUp className="w-4 h-4 text-emerald-600" />} testid="tile-dn-live" />
          <Tile label="Pipeline ARR" value={fmtUSD(portfolio.pipeline_annual_revenue)} icon={<BarChart3 className="w-4 h-4 text-amber-600" />} testid="tile-dn-pipeline" />
          <Tile label="Total ARR" value={fmtUSD(portfolio.total_annual_revenue)} icon={<Database className="w-4 h-4 text-cn-primary" />} testid="tile-dn-total" />
          <Tile label={`Gross profit @ ${portfolio.estimated_gross_margin_pct}%`} value={fmtUSD(portfolio.estimated_gross_profit_annual)} icon={<Banknote className="w-4 h-4 text-rose-600" />} testid="tile-dn-margin" />
        </div>
      )}

      {showCreate && (
        <form onSubmit={create} className="rounded-2xl bg-white border border-slate-100 p-5 mb-6" data-testid="dn-create-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input label="Counterparty" testid="dn-name" value={form.counterparty_name} onChange={(v) => setForm({ ...form, counterparty_name: v })} required />
            <Select label="Type" testid="dn-type" value={form.counterparty_type} onChange={(v) => setForm({ ...form, counterparty_type: v })}
              options={[
                { v: 'pharma', l: 'Pharma' }, { v: 'payer', l: 'Payer' }, { v: 'hospital', l: 'Hospital' },
                { v: 'pe_provider', l: 'PE-backed provider' }, { v: 'device', l: 'Device' },
                { v: 'government', l: 'Government' }, { v: 'academic', l: 'Academic' },
              ]} />
            <Select label="SKU" testid="dn-sku" value={form.sku} onChange={(v) => setForm({ ...form, sku: v })}
              options={Object.entries(SKU_LABELS).map(([v, l]) => ({ v, l }))} />
            <Input label="Quantity" type="number" testid="dn-qty" value={form.quantity} onChange={(v) => setForm({ ...form, quantity: v })} />
            <Select label="Stage" testid="dn-stage" value={form.stage} onChange={(v) => setForm({ ...form, stage: v })}
              options={STAGES.map((s) => ({ v: s, l: s }))} />
          </div>
          <div className="mt-4 flex gap-2">
            <Button type="submit" disabled={creating} className="bg-cn-primary text-white" data-testid="dn-submit">
              {creating ? 'Creating…' : 'Create contract'}
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
          </div>
        </form>
      )}

      <section className="rounded-2xl bg-white border border-slate-100 overflow-hidden" data-testid="dn-table">
        {loading ? (
          <div className="p-8 text-slate-500">Loading…</div>
        ) : contracts.length === 0 ? (
          <div className="p-12 text-center">
            <Database className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-700 font-semibold">No data contracts yet.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="text-left p-3 font-semibold">Counterparty</th>
                <th className="text-left p-3 font-semibold">SKU</th>
                <th className="text-right p-3 font-semibold">Qty</th>
                <th className="text-right p-3 font-semibold">Annual revenue</th>
                <th className="text-left p-3 font-semibold">Stage</th>
                <th className="text-right p-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`dn-row-${c.id}`}>
                  <td className="p-3">
                    <div className="font-semibold text-slate-900">{c.counterparty_name}</div>
                    <div className="text-xs text-slate-500 capitalize">{c.counterparty_type}</div>
                  </td>
                  <td className="p-3 text-slate-700">{SKU_LABELS[c.sku]}</td>
                  <td className="p-3 text-right tabular-nums">{c.quantity}</td>
                  <td className="p-3 text-right tabular-nums font-semibold text-cn-primary">{fmtUSD(c.annual_revenue)}</td>
                  <td className="p-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold ${STAGE_COLORS[c.stage]}`}>
                      {c.stage.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="p-3 text-right space-x-2">
                    {c.stage !== 'live' && c.stage !== 'churned' && (
                      <Button size="sm" variant="outline" onClick={() => advance(c.id, c.stage)} data-testid={`dn-advance-${c.id}`}>Advance →</Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => archive(c.id)} data-testid={`dn-archive-${c.id}`} className="text-rose-600 hover:text-rose-700">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </DashboardShell>
  );
}

function Tile({ label, value, icon, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
        {icon}
      </div>
      <div className="text-2xl font-extrabold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}

function Input({ label, value, onChange, type = 'text', required, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} data-testid={`input-${testid}`}
        className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-cn-primary" />
    </label>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={`input-${testid}`}
        className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-cn-primary">
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
